import get from 'lodash.get';
import { useState, useEffect, useContext, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { connect } from 'react-redux';
import classnames from 'classnames';

import { FormattedMessage, useIntl, defineMessages } from 'dibs-react-intl';
import { openLoginModal } from '../../actions/checkoutAuthActions';
import { removePromoCode, setPromoCode, setTriggeredErrors } from '../../actions/checkoutUIActions';
import { isLoggedIn } from '../../helpers/checkoutUser';
import { trackFormInteraction, trackPromoCodeSubmit } from '../../helpers/checkoutTracking';

// context
import { CheckoutServerVarsContext } from '../../components/shared/CheckoutServerVarsContext';
import { useCheckoutSelector, useAppDispatch } from '../../reducers/root';

import { Input } from 'dibs-elements/exports/Input';
import { Link } from 'dibs-elements/exports/Link';
import { Button } from 'dibs-elements/exports/Button';
import DisplayPromoCode from 'dibs-display-promo-code';
import Checkmark from 'dibs-icons/exports/legacy/Checkmark';

import styles from './styles/CheckoutSidebarPromotionForm.scss';

const messages = defineMessages({
    placeholder: {
        id: 'checkout.CheckoutSidebarPromotionForm.placeholder',
        defaultMessage: 'Enter Code',
    },
});

const CheckoutSidebarPromotionForm = props => {
    const [promoInput, setPromoInput] = useState('');
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const intl = useIntl();
    const environment = useRelayEnvironment();
    const {
        viewer: viewerRef,
        item: itemRef,
        transaction: transactionRef,
        promoCode,
        isComputing,
        onRemovePromoCode,
        onSetPromoCode,
        onPromoCodeSubmit,
        onPromoCodeRemove,
    } = props;

    const viewer = useFragment(
        graphql`
            fragment CheckoutSidebarPromotionForm_viewer on Viewer {
                activePromotion(userId: $buyerId, itemId: $itemId) {
                    code
                }
            }
        `,
        viewerRef
    );

    const item = useFragment(
        graphql`
            fragment CheckoutSidebarPromotionForm_item on Item {
                serviceId
            }
        `,
        itemRef
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutSidebarPromotionForm_transaction on Transaction {
                promotions {
                    code
                    isInvalid
                    hasBuyerError
                    terms
                    customerSuccessMessage
                    validationCode
                    failureSource {
                        sourceId
                    }
                    ruleList {
                        apiType
                        ... on PaymentMethodTypeRule {
                            serviceId
                            paymentMethodType
                        }
                    }
                }
                mostRecentOffer {
                    quantity
                }
            }
        `,
        transactionRef
    );

    const dispatch = useAppDispatch();
    const isProcessing = useCheckoutSelector(state => state.ui.isProcessing) || false;

    const { code, isInvalid, customerSuccessMessage, validationCode, failureSource, ruleList } =
        transaction.promotions[0] || {};
    const activePromoCode = viewer?.activePromotion?.code;
    const quantity = get(transaction, 'mostRecentOffer.quantity');
    const hasError = !quantity || !!(code && isInvalid && promoCode);
    const hasPromo = !!code && !hasError;
    const showErrorMessage =
        !quantity || (code && isInvalid && promoCode.toUpperCase() === code.toUpperCase());

    //check for Apple Pay promo rejection
    const failureSourceId = get(failureSource, 'sourceId');
    const applePayRuleObject =
        (ruleList || []).find(rule => rule.serviceId === failureSourceId) || {};
    const isApplePayPromoError =
        validationCode === 'PAYMENT_METHOD_TYPE_ERROR' &&
        applePayRuleObject.paymentMethodType === 'APPLE_PAY';

    const [shouldPrefillPromoCode, setShouldPrefillPromoCode] = useState(true);

    const onChange = event => {
        setPromoInput((event.target.value || '').trim());
        setShouldPrefillPromoCode(false);
    };

    const onRemove = event => {
        event.preventDefault();
        onRemovePromoCode();
        setPromoInput('');
        if (onPromoCodeRemove) {
            onPromoCodeRemove();
        }
    };
    const onSubmit = event => {
        event.preventDefault();

        if (promoInput) {
            trackFormInteraction('promo code section apply click');
            onSetPromoCode(promoInput);
            if (onPromoCodeSubmit) {
                onPromoCodeSubmit(promoInput);
            }
        }
    };

    const onPromoCodeRedeem = () => {
        if (hasPromo) {
            return onRemovePromoCode;
        } else {
            return (...args) => {
                trackFormInteraction('promo code section redeem click');
                onSetPromoCode(...args);
            };
        }
    };

    const handlePromoCodeFetch = useCallback(
        fetchedPromoCode => {
            if (shouldPrefillPromoCode) {
                setShouldPrefillPromoCode(false);
                setPromoInput(fetchedPromoCode);
            }
        },
        [shouldPrefillPromoCode]
    );

    useEffect(() => {
        if (showErrorMessage) {
            trackPromoCodeSubmit(null, code, { isInvalid, source: 'Checkout Sidebar' });
            dispatch(setTriggeredErrors(['promo-code']));
        }
    }, [showErrorMessage, code, isInvalid, dispatch]);

    const onFormSubmit = hasPromo ? onRemove : onSubmit;
    const isDiffPromoCode = code !== activePromoCode;
    const isSubmittedDiffPromoCode = hasPromo && isDiffPromoCode;
    const getErrorMessage = () => {
        const { onLogin } = props;
        const hasBuyerError = get(transaction, 'promotions[0].hasBuyerError');
        const hasQuantity = !!quantity;

        if (!isLoggedIn() && isInvalid && hasBuyerError) {
            return (
                <FormattedMessage
                    id="checkout.CheckoutSidebarPromotionForm.loginError"
                    defaultMessage="To apply a promotional code to your order, please {link} to your account."
                    values={{
                        link: (
                            <Link href="#login" onClick={e => onLogin(e)}>
                                <FormattedMessage
                                    id="checkout.CheckoutSidebarPromotionForm.login"
                                    defaultMessage="log in"
                                />
                            </Link>
                        ),
                    }}
                />
            );
        } else if (!hasQuantity) {
            return (
                <FormattedMessage
                    id="checkout.CheckoutSidebarPromotionForm.quantityError"
                    defaultMessage="Select a quantity above before entering a promotional code."
                />
            );
        } else if (isApplePayPromoError) {
            return (
                <FormattedMessage
                    id="checkout.CheckoutSidebarPromotionForm.applePayPromo"
                    defaultMessage="Before entering the promo code, please be sure you are in a Safari browser and have selected Apple Pay as your payment method."
                />
            );
        } else {
            return (
                <FormattedMessage
                    id="checkout.CheckoutSidebarPromotionForm.error"
                    defaultMessage="Promotional code {promoCode} is either invalid or expired."
                    values={{ promoCode }}
                />
            );
        }
    };

    const promoCodeFormattedMessage = hasPromo ? (
        <FormattedMessage
            id="checkout.CheckoutSidebarPromotionForm.remove"
            defaultMessage="Remove"
        />
    ) : (
        <FormattedMessage id="checkout.CheckoutSidebarPromotionForm.apply" defaultMessage="Apply" />
    );

    return (
        <Fragment>
            <form
                className={classnames([hasPromo ? styles.columns : styles.rows, styles.form])}
                onSubmit={onFormSubmit}
            >
                {(isSubmittedDiffPromoCode || (hasPromo && !isMobile)) && (
                    <>
                        <Checkmark className={styles.checkmark} />
                        <div className={styles.customerSuccessMessage}>
                            <FormattedMessage
                                id="checkout.CheckoutSidebarPromotionForm.customerSuccessMessage"
                                defaultMessage="{customerSuccessMessage}"
                                values={{ customerSuccessMessage }}
                            />
                        </div>

                        <Link
                            className={styles.removeLink}
                            size="medium"
                            dataTn={`${hasPromo ? 'remove' : 'add'}-promo-code-button`}
                            disabled={isComputing || promoInput.length === 0 || isProcessing}
                            onClick={onFormSubmit}
                        >
                            {promoCodeFormattedMessage}
                        </Link>
                    </>
                )}
                {!hasPromo && (
                    <Input
                        type="text"
                        value={promoInput}
                        size="medium"
                        placeholder={intl.formatMessage(messages.placeholder)}
                        theme={{ fieldContainer: styles.input }}
                        dataTn="add-promo-code-input"
                        onChange={e => onChange(e)}
                    />
                )}
                {!hasPromo && !isMobile && (
                    <Button
                        size="medium"
                        dataTn={`${hasPromo ? 'remove' : 'add'}-promo-code-button`}
                        disabled={isComputing || promoInput.length === 0}
                        onClick={onFormSubmit}
                    >
                        {promoCodeFormattedMessage}
                    </Button>
                )}
            </form>
            {showErrorMessage && (
                <div
                    data-tn="checkout-promo-form-error"
                    className={classnames(styles.error, { [styles.desktopError]: !isMobile })}
                >
                    {getErrorMessage()}
                </div>
            )}
            {!hasPromo && (
                <div className={styles.code}>
                    <DisplayPromoCode
                        environment={environment}
                        itemId={item.serviceId}
                        onPromoCodeRedeem={
                            isMobile && hasPromo && !isSubmittedDiffPromoCode
                                ? onPromoCodeRedeem()
                                : null
                        }
                        onPromoCodeFetch={handlePromoCodeFetch}
                        hasPromo={hasPromo}
                        hasError={hasError}
                        showDetails={!hasPromo || !isMobile || isSubmittedDiffPromoCode}
                        redeemPromoCodeClass={classnames({
                            [styles.redeemPromoCodeClass]: isMobile,
                        })}
                        promoCodeClass={classnames({ [styles.promoCodeClass]: isMobile })}
                        redeemPromoCodeSpinnerClass={classnames({
                            [styles.redeemPromoCodeSpinnerClass]: isMobile,
                        })}
                    />
                </div>
            )}
        </Fragment>
    );
};

CheckoutSidebarPromotionForm.propTypes = {
    viewer: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired,
    buyer: PropTypes.object,
    transaction: PropTypes.object.isRequired,
    promoCode: PropTypes.string.isRequired,
    isComputing: PropTypes.bool.isRequired,
    onLogin: PropTypes.func.isRequired,
    onRemovePromoCode: PropTypes.func.isRequired,
    onSetPromoCode: PropTypes.func.isRequired,
    onPromoCodeFetch: PropTypes.func,
    onPromoCodeSubmit: PropTypes.func,
    onPromoCodeRemove: PropTypes.func,
};

export default connect(
    ({ transaction }) => ({
        promoCode: transaction.promoCode || '',
    }),
    dispatch => ({
        onSetPromoCode: (...args) => dispatch(setPromoCode(...args)),
        onRemovePromoCode: (...args) => dispatch(removePromoCode(...args)),
        onLogin: e => {
            e.preventDefault();
            openLoginModal({ dispatch });
        },
    })
)(CheckoutSidebarPromotionForm);
